import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useFristApprovalOptional } from './FristApprovalContext';
import {
  createEigenBenannteFristForAkte,
  createPortalFristForAkte,
  deleteEigenBenannteFristForAkte,
  listPortalFristenForAkteResult,
  setEigenBenannteFristScharf,
  updateEigenBenannteFristForAkte,
  updatePortalFristForAkte,
} from './portalFristen';
import {
  PORTAL_EIGEN_FRIST_REGEL_ID,
  eigenFristErinnerungDraft,
  eigenFristIsPrepared,
  eigenFristReadBackOk,
} from './portalEigenFrist';
import {
  PORTAL_FRISTEN_CRUD_TYPES,
  portalFristRecordBelongsToWriteScope,
  resolvePortalFristenCrudType,
  validatePortalFristWriteScope,
} from './portalFristenCrud';
import type { PortalFristRecord } from './fristApprovalTypes';

const MIN_RELOAD_INTERVAL_MS = 30_000;

function formatDeDate(iso: string): string {
  const d = iso.trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}.${m}.${y}`;
}

function normalizeAkteKey(path?: string | null): string {
  return String(path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    .toLowerCase();
}

function buildResolvedCardsSignature(
  cards: Record<string, { status: string; resolvedAt?: string }> | undefined,
): string {
  if (!cards) return '';
  return Object.entries(cards)
    .filter(([, c]) => c.status === 'approved' || c.status === 'revoked')
    .map(([id, c]) => `${id}:${c.status}:${c.resolvedAt ?? ''}`)
    .sort()
    .join('|');
}

export interface PortalFristenAktePanelProps {
  customerId: string;
  authToken?: string | null;
  /** S2: Pflicht für jeden create/update-Write. */
  akteId?: string | null;
  akteNcPath?: string | null;
  className?: string;
  /** `strip` = ohne äußeren Rahmen im Collapsible-Strip; `hidden` = nur Daten/Summary. */
  embedMode?: 'standalone' | 'strip' | 'hidden';
  onCollapsedSummaryChange?: (summary: string | null) => void;
  emptyMessage?: string;
  /** S2 nur im Akt-Hub aktivieren; andere Fristenanzeigen bleiben read-only. */
  allowCrud?: boolean;
  canWrite?: boolean;
  actorEmail?: string;
  tenant?: string;
  customerStatus?: string;
}

/**
 * Aktive Fristen der geöffneten Akte (PB `portal_fristen` oder Demo-Mock).
 */
function buildFristenCollapsedSummary(
  items: PortalFristRecord[],
  loading: boolean,
  loadedOnce: boolean,
): string | null {
  if (!loadedOnce && loading) return null;
  if (!items.length) {
    return loadedOnce ? 'Keine aktiven Fristen' : null;
  }
  const first = items[0];
  const tail = items.length > 1 ? ` · +${items.length - 1}` : '';
  return `${items.length} Frist${items.length !== 1 ? 'en' : ''} · ${first.frist_typ}${tail}`;
}

export const PortalFristenAktePanel: React.FC<PortalFristenAktePanelProps> = ({
  customerId,
  authToken,
  akteId,
  akteNcPath,
  className = '',
  embedMode = 'standalone',
  onCollapsedSummaryChange,
  emptyMessage = 'Keine aktiven Fristen gespeichert. Fristen erscheinen nach Bestätigung im ARKO-Chat.',
  allowCrud = false,
  canWrite = false,
  actorEmail,
  tenant,
  customerStatus,
}) => {
  const ctx = useFristApprovalOptional();
  const [items, setItems] = useState<PortalFristRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'update' | null>(null);
  const [editingRecord, setEditingRecord] = useState<PortalFristRecord | null>(null);
  const [fristTypeId, setFristTypeId] = useState<string>(PORTAL_FRISTEN_CRUD_TYPES[0].id);
  const [startDatum, setStartDatum] = useState('');
  const [eigenOpen, setEigenOpen] = useState(false);
  const [eigenEditingId, setEigenEditingId] = useState('');
  const [eigenName, setEigenName] = useState('');
  const [eigenDatum, setEigenDatum] = useState('');
  const [eigenErinnerung, setEigenErinnerung] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadedOnceRef = useRef(false);
  const lastReloadAtRef = useRef(0);
  const resolvedSignatureRef = useRef('');
  const akteNcPathRef = useRef(akteNcPath);
  const akteIdRef = useRef(akteId);
  const authTokenRef = useRef(authToken);
  akteNcPathRef.current = akteNcPath;
  akteIdRef.current = akteId;
  authTokenRef.current = authToken;

  const akteKey = useMemo(() => normalizeAkteKey(akteNcPath), [akteNcPath]);
  const writeScope = useMemo(
    () =>
      validatePortalFristWriteScope({
        customerId,
        akteId,
        akteNcPath,
        // Der Parent reicht ausschließlich die bereits sichtbar aufgelöste Hub-Akte.
        visibleAkteId: akteId,
        visibleAkteNcPath: akteNcPath,
      }),
    [akteId, akteNcPath, customerId],
  );
  const crudReady =
    allowCrud &&
    canWrite &&
    Boolean(authToken?.trim()) &&
    writeScope.ok;

  const resolvedSignature = useMemo(
    () => buildResolvedCardsSignature(ctx?.cards),
    [ctx?.cards],
  );

  const reload = useCallback(
    async (options?: { silent?: boolean; force?: boolean }) => {
      if (!customerId?.trim()) {
        return { ok: false as const, error: 'Mandant fehlt — Fristen wurden nicht geladen.' };
      }

      const now = Date.now();
      if (!options?.force && now - lastReloadAtRef.current < MIN_RELOAD_INTERVAL_MS) {
        return { ok: true as const, rows: [] as PortalFristRecord[], throttled: true as const };
      }
      lastReloadAtRef.current = now;

      const showSpinner = !options?.silent && !loadedOnceRef.current;
      if (showSpinner) setLoading(true);
      try {
        const result = await listPortalFristenForAkteResult(
          customerId,
          authTokenRef.current?.trim() ?? '',
          akteNcPathRef.current?.trim() || undefined,
          akteIdRef.current?.trim() || undefined,
        );
        if (result.ok === false) {
          setLoadError(result.error);
          return result;
        }
        setItems(result.data);
        setLoadError(null);
        loadedOnceRef.current = true;
        setLoadedOnce(true);
        return { ok: true as const, rows: result.data };
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [customerId],
  );

  const openCreateEditor = useCallback(() => {
    setEigenOpen(false);
    setEigenEditingId('');
    setEditingRecord(null);
    setEditorMode('create');
    setFristTypeId(PORTAL_FRISTEN_CRUD_TYPES[0].id);
    setStartDatum('');
    setSaveMessage(null);
  }, []);

  const openEigenEditor = useCallback(() => {
    setEditorMode(null);
    setEditingRecord(null);
    setEigenEditingId('');
    setEigenName('');
    setEigenDatum('');
    setEigenErinnerung('');
    setEigenOpen(true);
    setSaveMessage(null);
  }, []);

  const openEigenUpdate = useCallback((record: PortalFristRecord) => {
    if (record.regel_id !== PORTAL_EIGEN_FRIST_REGEL_ID) return;
    if ((record.akte_id || '').trim() !== (akteId || '').trim()) return;
    setEditorMode(null);
    setEditingRecord(null);
    setEigenEditingId(record.id);
    setEigenName(record.frist_typ);
    setEigenDatum(record.start_datum.slice(0, 10));
    setEigenErinnerung(eigenFristErinnerungDraft(record.start_datum, record.ende_datum));
    setEigenOpen(true);
    setSaveMessage(null);
  }, [akteId]);

  const saveEigenFrist = useCallback(async () => {
    if (writeScope.ok === false) {
      setSaveMessage({ kind: 'error', text: writeScope.error });
      return;
    }
    if (!crudReady) {
      setSaveMessage({
        kind: 'error',
        text: 'Kein Schreibrecht oder keine gültige Portal-Sitzung.',
      });
      return;
    }
    setSaveBusy(true);
    setSaveMessage(null);
    try {
      const editing = items.find((row) => row.id === eigenEditingId);
      const result = eigenEditingId
        ? editing && editing.regel_id === PORTAL_EIGEN_FRIST_REGEL_ID && editing.akte_id === writeScope.akteId
          ? await updateEigenBenannteFristForAkte({
              authToken: authToken?.trim() || '',
              recordId: editing.id,
              akteId: writeScope.akteId,
              name: eigenName,
              datum: eigenDatum,
              erinnerung: eigenErinnerung,
              scharf: !eigenFristIsPrepared(editing.kalender_trace),
            })
          : { ok: false as const, error: 'Frist gehört nicht zu dieser Akte — Änderung verweigert.' }
        : await createEigenBenannteFristForAkte({
            customerId: writeScope.customerId,
            akte_id: writeScope.akteId,
            akte_nc_path: writeScope.akteNcPath,
            name: eigenName,
            datum: eigenDatum,
            erinnerung: eigenErinnerung,
            bestaetigt_von: actorEmail?.trim() || undefined,
            tenant,
            customerStatus,
          });
      if (result.ok === false) {
        setSaveMessage({
          kind: 'error',
          text: result.error || 'Frist wurde nicht gespeichert.',
        });
        return;
      }
      lastReloadAtRef.current = 0;
      const verified = await reload({ force: true });
      const persisted =
        verified.ok &&
        eigenFristReadBackOk(
          verified.rows,
          writeScope.akteId,
          eigenName.trim(),
          result.pbRecordId || '',
        );
      if (!persisted) {
        setSaveMessage({
          kind: 'error',
          text:
            verified.ok === false
              ? `Speichern konnte beim Neuladen nicht bestätigt werden: ${verified.error}`
              : 'Speichern konnte beim Neuladen nicht bestätigt werden — die Frist bleibt offen.',
        });
        return;
      }
      setSaveMessage({
        kind: 'success',
        text: eigenEditingId
          ? 'Eigene Frist wurde in dieser Akte geändert.'
          : 'Eigene Frist wurde in dieser Akte gespeichert.',
      });
      setEigenOpen(false);
      setEigenEditingId('');
      setEigenName('');
      setEigenDatum('');
      setEigenErinnerung('');
    } finally {
      setSaveBusy(false);
    }
  }, [
    actorEmail,
    crudReady,
    customerStatus,
    authToken,
    eigenDatum,
    eigenEditingId,
    eigenErinnerung,
    eigenName,
    items,
    reload,
    tenant,
    writeScope,
  ]);

  const deleteEigenFrist = useCallback(async (record: PortalFristRecord) => {
    if (writeScope.ok === false || !crudReady || saveBusy) return;
    if ((record.akte_id || '').trim() !== writeScope.akteId) {
      setSaveMessage({ kind: 'error', text: 'Frist gehört nicht zu dieser Akte — Löschen verweigert.' });
      return;
    }
    setSaveBusy(true);
    setSaveMessage(null);
    try {
      const result = await deleteEigenBenannteFristForAkte({
        authToken: authToken?.trim() || '',
        recordId: record.id,
        akteId: writeScope.akteId,
      });
      if (result.ok === false) {
        setSaveMessage({ kind: 'error', text: result.error || 'Frist wurde nicht gelöscht.' });
        return;
      }
      lastReloadAtRef.current = 0;
      const verified = await reload({ force: true });
      const gone = verified.ok && !verified.rows.some((row) => row.id === record.id);
      if (!gone) {
        setSaveMessage({
          kind: 'error',
          text: verified.ok === false
            ? `Löschen konnte beim Neuladen nicht bestätigt werden: ${verified.error}`
            : 'Löschen konnte beim Neuladen nicht bestätigt werden — die Frist bleibt offen.',
        });
        return;
      }
      if (eigenEditingId === record.id) {
        setEigenOpen(false);
        setEigenEditingId('');
      }
      setSaveMessage({ kind: 'success', text: 'Frist wurde in dieser Akte gelöscht.' });
    } finally {
      setSaveBusy(false);
    }
  }, [authToken, crudReady, eigenEditingId, reload, saveBusy, writeScope]);

  const toggleEigenScharf = useCallback(async (record: PortalFristRecord, scharf: boolean) => {
    if (writeScope.ok === false || !crudReady || saveBusy) return;
    if ((record.akte_id || '').trim() !== writeScope.akteId) return;
    setSaveBusy(true);
    setSaveMessage(null);
    try {
      const result = await setEigenBenannteFristScharf({
        authToken: authToken?.trim() || '',
        recordId: record.id,
        akteId: writeScope.akteId,
        trace: record.kalender_trace,
        scharf,
      });
      if (result.ok === false) {
        setSaveMessage({ kind: 'error', text: result.error || 'Der Haken wurde nicht gespeichert.' });
        return;
      }
      lastReloadAtRef.current = 0;
      const verified = await reload({ force: true });
      const row = verified.ok ? verified.rows.find((item) => item.id === record.id) : undefined;
      if (!row || row.akte_id !== writeScope.akteId || eigenFristIsPrepared(row.kalender_trace) === scharf) {
        setSaveMessage({
          kind: 'error',
          text: 'Der Haken konnte beim Neuladen nicht bestätigt werden — die Frist bleibt offen.',
        });
      }
    } finally {
      setSaveBusy(false);
    }
  }, [authToken, crudReady, reload, saveBusy, writeScope]);

  const openUpdateEditor = useCallback((record: PortalFristRecord) => {
    const option = resolvePortalFristenCrudType(record);
    if (!option) {
      setSaveMessage({
        kind: 'error',
        text: 'Dieser Fristentyp lässt sich hier nicht ändern.',
      });
      return;
    }
    setEditingRecord(record);
    setEditorMode('update');
    setFristTypeId(option.id);
    setStartDatum(record.start_datum.slice(0, 10));
    setSaveMessage(null);
  }, []);

  const closeEditor = useCallback(() => {
    if (saveBusy) return;
    setEditorMode(null);
    setEditingRecord(null);
    setSaveMessage(null);
  }, [saveBusy]);

  const saveFrist = useCallback(async () => {
    if (writeScope.ok === false) {
      setSaveMessage({ kind: 'error', text: writeScope.error });
      return;
    }
    if (!crudReady) {
      setSaveMessage({
        kind: 'error',
        text: 'Kein Schreibrecht oder keine gültige Portal-Sitzung.',
      });
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDatum)) {
      setSaveMessage({ kind: 'error', text: 'Bitte ein gültiges Startdatum wählen.' });
      return;
    }
    const option = PORTAL_FRISTEN_CRUD_TYPES.find((row) => row.id === fristTypeId);
    if (!option) {
      setSaveMessage({ kind: 'error', text: 'Unbekannter Fristentyp.' });
      return;
    }

    const input = {
      customerId: writeScope.customerId,
      akte_id: writeScope.akteId,
      akte_nc_path: writeScope.akteNcPath,
      frist_typ_vorschlag: option.label,
      start_datum: startDatum,
      regel_id: option.regel_id,
      bestaetigt_von: actorEmail?.trim() || undefined,
      tenant,
      customerStatus,
    };

    setSaveBusy(true);
    setSaveMessage(null);
    try {
      const result =
        editorMode === 'update'
          ? editingRecord &&
            portalFristRecordBelongsToWriteScope(editingRecord, writeScope)
            ? await updatePortalFristForAkte({
                ...input,
                pb_record_id: editingRecord.id,
              })
            : {
                ok: false as const,
                error: 'Frist gehört nicht zur geöffneten Akte — Änderung verweigert.',
              }
          : await createPortalFristForAkte(input);

      if (result.ok === false) {
        setSaveMessage({
          kind: 'error',
          text: result.error || 'Frist wurde nicht gespeichert.',
        });
        return;
      }

      lastReloadAtRef.current = 0;
      const verified = await reload({ force: true });
      const persisted =
        verified.ok &&
        Boolean(result.pbRecordId) &&
        verified.rows.some((row) => row.id === result.pbRecordId);
      if (!persisted) {
        setSaveMessage({
          kind: 'error',
          text:
            verified.ok === false
              ? `Speichern konnte beim Neuladen nicht bestätigt werden: ${verified.error}`
              : 'Speichern konnte beim Neuladen nicht bestätigt werden — die Frist bleibt offen.',
        });
        return;
      }

      setSaveMessage({
        kind: 'success',
        text: editorMode === 'update' ? 'Frist wurde geändert.' : 'Frist wurde angelegt.',
      });
      setEditorMode(null);
      setEditingRecord(null);
    } finally {
      setSaveBusy(false);
    }
  }, [
    actorEmail,
    crudReady,
    customerStatus,
    editingRecord,
    editorMode,
    fristTypeId,
    reload,
    startDatum,
    tenant,
    writeScope,
  ]);

  useEffect(() => {
    lastReloadAtRef.current = 0;
    loadedOnceRef.current = false;
    setLoadedOnce(false);
    setEditorMode(null);
    setEditingRecord(null);
    setEigenOpen(false);
    setEigenEditingId('');
    setEigenName('');
    setEigenDatum('');
    setEigenErinnerung('');
    setSaveMessage(null);
    setLoadError(null);
    void reload({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akteKey, customerId]);

  useEffect(() => {
    if (!resolvedSignature) return;
    if (resolvedSignatureRef.current === resolvedSignature) return;
    resolvedSignatureRef.current = resolvedSignature;
    lastReloadAtRef.current = 0;
    void reload({ silent: true, force: true });
  }, [resolvedSignature, reload]);

  const collapsedSummary = buildFristenCollapsedSummary(items, loading, loadedOnce);

  useEffect(() => {
    onCollapsedSummaryChange?.(collapsedSummary);
  }, [collapsedSummary, onCollapsedSummaryChange]);

  if (embedMode === 'hidden') return null;

  if (!akteNcPath?.trim() && items.length === 0 && !loadedOnce) return null;

  const shellClass =
    embedMode === 'strip'
      ? `px-3 py-2 min-h-[3.25rem] ${className}`
      : `rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 min-h-[3.25rem] ${className}`;

  return (
    <div
      className={shellClass}
      aria-label="Aktive Fristen in dieser Akte"
    >
      {embedMode !== 'strip' ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" aria-hidden />
            Fristen in dieser Akte
          </p>
          <div className="flex items-center gap-1.5">
            {allowCrud && canWrite ? (
              <>
                <button
                  type="button"
                  data-testid="portal-frist-create"
                  onClick={openCreateEditor}
                  disabled={!crudReady || saveBusy}
                  className="inline-flex items-center gap-1 rounded-md border border-violet-400/35 bg-violet-500/10 px-2 py-1 text-[10px] font-semibold text-violet-200 hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <Plus className="h-3 w-3" aria-hidden />
                  Frist anlegen
                </button>
                <button
                  type="button"
                  data-testid="portal-eigen-frist-open"
                  onClick={openEigenEditor}
                  disabled={!crudReady || saveBusy}
                  className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[10px] font-semibold text-slate-200 hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  Eigene Frist
                </button>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                lastReloadAtRef.current = 0;
                void reload({ force: true });
              }}
              disabled={loading}
              className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
              title="Fristen neu laden"
              aria-label="Fristen neu laden"
            >
              {loading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              lastReloadAtRef.current = 0;
              void reload({ force: true });
            }}
            disabled={loading}
            className="p-0.5 text-slate-500 hover:text-slate-300 disabled:opacity-50"
            title="Fristen neu laden"
            aria-label="Fristen neu laden"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
          </button>
        </div>
      )}

      {allowCrud && canWrite && writeScope.ok === false ? (
        <p
          role="alert"
          data-testid="portal-frist-scope-error"
          className="mt-2 rounded-md border border-amber-500/25 bg-amber-500/5 px-2.5 py-2 text-[11px] text-amber-200"
        >
          {writeScope.error}
        </p>
      ) : null}

      {allowCrud && !canWrite ? (
        <p className="mt-2 text-[11px] text-slate-500">
          Fristen sind für diesen Sitz nur lesbar.
        </p>
      ) : null}

      {loadError ? (
        <p
          role="alert"
          data-testid="portal-frist-load-error"
          className="mt-2 rounded-md border border-red-500/25 bg-red-500/5 px-2.5 py-2 text-[11px] text-red-200"
        >
          {loadError}
        </p>
      ) : null}

      {editorMode ? (
        <form
          data-testid="portal-frist-form"
          className="mt-2 space-y-2 rounded-lg border border-violet-400/20 bg-violet-500/[0.06] p-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            void saveFrist();
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="m-0 text-[11px] font-semibold text-slate-200">
              {editorMode === 'update' ? 'Frist ändern' : 'Neue Frist'}
            </p>
            <button
              type="button"
              onClick={closeEditor}
              disabled={saveBusy}
              className="rounded p-0.5 text-slate-500 hover:text-slate-200 disabled:opacity-50"
              aria-label="Fristenformular schließen"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          <label className="block space-y-1 text-[10px] font-medium text-slate-400">
            Fristentyp
            <select
              value={fristTypeId}
              onChange={(event) => setFristTypeId(event.target.value)}
              disabled={saveBusy}
              className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-1.5 text-[11px] text-slate-200 outline-none focus:border-violet-400/60"
            >
              {PORTAL_FRISTEN_CRUD_TYPES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-[10px] font-medium text-slate-400">
            Auslösendes Datum
            <input
              type="date"
              required
              value={startDatum}
              onChange={(event) => setStartDatum(event.target.value)}
              disabled={saveBusy}
              className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-1.5 text-[11px] text-slate-200 outline-none focus:border-violet-400/60"
            />
          </label>
          <p className="m-0 text-[10px] text-slate-500">
            Das Enddatum berechnet sich fest aus Typ und Startdatum.
          </p>
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={closeEditor}
              disabled={saveBusy}
              className="rounded-md border border-white/10 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              data-testid="portal-frist-save"
              disabled={saveBusy || !crudReady}
              className="inline-flex items-center gap-1 rounded-md border border-violet-400/40 bg-violet-500/15 px-2.5 py-1 text-[10px] font-semibold text-violet-100 hover:bg-violet-500/25 disabled:opacity-50"
            >
              {saveBusy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              {editorMode === 'update' ? 'Änderung speichern' : 'Frist speichern'}
            </button>
          </div>
        </form>
      ) : null}

      {eigenOpen ? (
        <form
          data-testid="portal-eigen-frist-form"
          className="mt-2 space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            void saveEigenFrist();
          }}
        >
          <p className="m-0 text-[11px] font-semibold text-slate-200">
            {eigenEditingId ? 'Eigene Frist ändern' : 'Eigene Frist in dieser Akte'}
          </p>
          <label className="block space-y-1 text-[10px] font-medium text-slate-400">
            Name
            <input
              type="text"
              data-testid="portal-eigen-frist-name"
              value={eigenName}
              onChange={(event) => setEigenName(event.target.value)}
              disabled={saveBusy}
              maxLength={200}
              className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-1.5 text-[11px] text-slate-200 outline-none focus:border-violet-400/60"
            />
          </label>
          <label className="block space-y-1 text-[10px] font-medium text-slate-400">
            Datum
            <input
              type="date"
              data-testid="portal-eigen-frist-datum"
              value={eigenDatum}
              onChange={(event) => setEigenDatum(event.target.value)}
              disabled={saveBusy}
              className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-1.5 text-[11px] text-slate-200 outline-none focus:border-violet-400/60"
            />
          </label>
          <label className="block space-y-1 text-[10px] font-medium text-slate-400">
            Erinnerung
            <input
              type="date"
              data-testid="portal-eigen-frist-erinnerung"
              value={eigenErinnerung}
              onChange={(event) => setEigenErinnerung(event.target.value)}
              disabled={saveBusy}
              className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-1.5 text-[11px] text-slate-200 outline-none focus:border-violet-400/60"
            />
          </label>
          <p className="m-0 text-[10px] text-slate-500">
            Sichtbar in dieser Akte. Der Kalender zeigt denselben Datensatz, wenn er Aktenfristen schon zeigt.
          </p>
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => {
                if (saveBusy) return;
                setEigenOpen(false);
                setEigenEditingId('');
              }}
              disabled={saveBusy}
              className="rounded-md border border-white/10 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              data-testid="portal-eigen-frist-save"
              disabled={saveBusy || !crudReady}
              className="inline-flex items-center gap-1 rounded-md border border-violet-400/40 bg-violet-500/15 px-2.5 py-1 text-[10px] font-semibold text-violet-100 hover:bg-violet-500/25 disabled:opacity-50"
            >
              {saveBusy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              {eigenEditingId ? 'Änderung speichern' : 'Frist speichern'}
            </button>
          </div>
        </form>
      ) : null}

      {saveMessage ? (
        <p
          role={saveMessage.kind === 'error' ? 'alert' : 'status'}
          className={`mt-2 rounded-md border px-2.5 py-2 text-[11px] ${
            saveMessage.kind === 'error'
              ? 'border-red-500/25 bg-red-500/5 text-red-200'
              : 'border-emerald-500/25 bg-emerald-500/5 text-emerald-200'
          }`}
        >
          {saveMessage.text}
        </p>
      ) : null}

      {items.length === 0 ? (
        <p className="text-[11px] text-slate-500 m-0 mt-1.5">
          {loading ? 'Lade…' : emptyMessage}
        </p>
      ) : (
        <ul className="m-0 mt-1.5 p-0 list-none space-y-1.5">
          {items.map((f) => {
            const eigen = f.regel_id === PORTAL_EIGEN_FRIST_REGEL_ID;
            const prepared = eigenFristIsPrepared(f.kalender_trace);
            const zeileIcon =
              'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:opacity-45';
            return (
            <li
              key={f.id}
              className="flex items-center gap-1.5 border-t border-white/[0.04] pt-1.5 text-[11px] text-slate-300 first:border-0 first:pt-0"
            >
              <input
                type="checkbox"
                data-testid={`portal-eigen-frist-scharf-${f.id}`}
                checked={!prepared}
                disabled={!crudReady || saveBusy}
                title="scharf"
                aria-label="scharf"
                onChange={(event) => void toggleEigenScharf(f, event.target.checked)}
                className="h-3.5 w-3.5 shrink-0 accent-violet-500"
              />
              <button
                type="button"
                data-testid={eigen ? `portal-eigen-frist-edit-${f.id}` : `portal-frist-edit-${f.id}`}
                title="Ändern"
                aria-label="Ändern"
                onClick={() => (f.regel_id !== PORTAL_EIGEN_FRIST_REGEL_ID ? openUpdateEditor(f) : openEigenUpdate(f))}
                disabled={!crudReady || saveBusy}
                className={zeileIcon}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </button>
              <button
                type="button"
                data-testid={`portal-eigen-frist-delete-${f.id}`}
                title="Löschen"
                aria-label="Löschen"
                onClick={() => void deleteEigenFrist(f)}
                disabled={!crudReady || saveBusy}
                className={zeileIcon}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
              <span className="min-w-0">
                <span className={`font-medium ${prepared ? 'text-slate-400' : 'text-slate-200'}`}>{f.frist_typ}</span>
                <span className="text-slate-500">
                  {' '}
                  · {formatDeDate(f.start_datum)} → {formatDeDate(f.ende_datum)}
                  {prepared ? ' · vorbereitet' : ''}
                </span>
              </span>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
